import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';

import { EmptySelect } from './emptySelect';

import type { AzureDiskConfigState } from '../restore/restoreAzureVMWizard';

const title = 'Virtual Machine Size';
export const VirtualMachineSizeSelect = ({
  accountId,
  location,
  vmSize,
  onChange,
  disksState,
  originalVMSize,
}: {
  accountId?: string;
  location?: string;
  vmSize?: string;
  disksState: AzureDiskConfigState[] | undefined;
  onChange: (vmSize: string) => void;
  originalVMSize: string | undefined;
}) => {
  if (!accountId || !location) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerVirtualMachineSizeSelect
      accountId={accountId}
      location={location}
      vmSize={vmSize}
      onChange={onChange}
      disksState={disksState}
      originalVMSize={originalVMSize}
    />
  );
};

const InnerVirtualMachineSizeSelect = ({
  accountId,
  location,
  vmSize,
  onChange,
  disksState,
  originalVMSize,
}: {
  accountId: string;
  location: string;
  vmSize?: string;
  disksState: AzureDiskConfigState[] | undefined;
  onChange: (vmSize: string) => void;
  originalVMSize: string | undefined;
}) => {
  const dal = useDAL();
  const { isDemo, isDev } = useEnvironment();
  const { data: body } = dal.restore.azureRestoreConfig.useListSKUs(
    accountId,
    location,
    ResourceType.AzureVirtualMachine
  );

  const SKUChoices = useMemo(() => {
    const dataDisksLength = (disksState?.length ?? 1) - 1;
    const originalVMSKU = body?.skus?.find((x) => x.name === originalVMSize);
    const diskHyperVGenerations = Array.from(
      new Set(disksState?.map((d) => d.diskSettings.settings.hyperVGeneration))
    ).filter(Boolean);
    return (body?.skus ?? [])
      .filter((sku) => {
        if (isDemo || isDev) {
          return true;
        }
        const skuGenerations = sku.capabilities?.HyperVGenerations?.split(',');
        return (
          (sku.capabilities?.CpuArchitectureType ?? '') ===
            (originalVMSKU?.capabilities?.CpuArchitectureType ?? '') &&
          Number(sku.capabilities?.MaxDataDiskCount ?? '') >= dataDisksLength &&
          diskHyperVGenerations.every(
            (g) =>
              typeof g === 'string' &&
              Array.isArray(skuGenerations) &&
              skuGenerations.includes(g)
          )
        );
      })
      .map((sku) => sku.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disksState, body?.skus, originalVMSize]);

  if (!body?.skus?.length) {
    return <EmptySelect title={title} />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        clearOnBlur
        size='small'
        value={vmSize || null}
        onChange={(event, newValue) => onChange(newValue || '')}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              sx={{
                '& .MuiInputBase-input': {
                  height: '40px',
                },
              }}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={SKUChoices}
      />
    </FormControl>
  );
};
